import {
  Box,
  Typography,
  Button,
  TextField,
  Tooltip,
  FormControl,
  MenuItem,
  Select,
  Divider,
  Switch,
} from "@mui/material";
import React, {
  FC,
  ReactElement,
  useContext,
  useState,
  useEffect,
} from "react";
import { MeetingItemRepresentation } from "../models/meetingItemRepresentation";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import ItemStatus from "./itemStatus";
import dateFormatter, { dateTimeFormatter } from "../util/dateFormatter";
import SendIcon from "@mui/icons-material/Send";
import { AppContext } from "../models/applicationState";
import { Context } from "./context";
import { useMsal } from "@azure/msal-react";
import {
  Meeting,
  MeetingItem,
  RiskIndicator,
  enumFromStringValue,
} from "../models";
import { FreeSingleSelect } from "./freeSingleSelect";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import { AttachmentsModal } from "./attachmentsModal";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ConfirmationPanel from "./confirmationPanel";
import { PaperComponent } from "../util/paperComponent";
import PersonalNotesModal from "./meetingNotes/PersonalNotesModal";
import { PersonalNote, PersonalNoteTargetType } from "../models/personalNote";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

interface ItemEditingPanelSimpleProps {
  meetingItem: MeetingItemRepresentation;
  open: boolean;
  meeting?: Meeting;
  allowMetadataEditing?: boolean;
  allowAttachmentsEditing?: boolean;
  readOnly?: boolean;
  onClose: () => Promise<void> | void;
  onUpdate: (item: MeetingItemRepresentation) => Promise<void> | void;
  onAttachmentsCountChanged?: (
    count: number,
    meetingId?: string,
    itemId?: string,
    sessionId?: string,
    taskId?: string,
  ) => Promise<void> | void;
}

const systemCommentMarkers = (t: TFunction<"translation", undefined>) => [
  t("item.status_changed"),
  t("meeting_status.published"),
  t("item.dueDate_changed"),
  t("item.risk_changed"),
  t("item.content_changed"),
  t("item.prioritization_changed"),
  t("item.actor_changed"),
];

const isSystemCommentLine = (
  line: string,
  t: TFunction<"translation", undefined>,
) => {
  const markers = systemCommentMarkers(t)
    .map((m) => m?.toLowerCase())
    .filter(Boolean);
  const text = line.replace(/^\[.*?\]:\s*/, "").toLowerCase();
  return markers.some(
    (marker) => text === marker || text.startsWith(`${marker}:`),
  );
};

const splitCommentLines = (comments?: string) =>
  (comments || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

export const hasUserCommentsSimple = (
  item: MeetingItemRepresentation,
  t: TFunction<"translation", undefined>,
) => {
  if (!item?.comments) {
    return false;
  }
  return splitCommentLines(item.comments).some(
    (line) => !isSystemCommentLine(line, t),
  );
};
const addCommentToItem = (
  item: MeetingItemRepresentation,
  userFullName: string,
  comment: string,
  t: TFunction<"translation", undefined>,
) => {
  addCommentToItemInternal(item, userFullName, comment);
};

export const addStatusUpdateSystemCommentToItemSimple = (
  item: MeetingItemRepresentation,
  userFullName: string,
  t: TFunction<"translation", undefined>,
) => {
  addCommentToItemInternal(
    item,
    userFullName,
    t("item.status_changed") + ": " + t(`item_status.${item.status}`),
  );
};

export const addPublishSystemCommentToItemSimple = (
  item: MeetingItemRepresentation | MeetingItem,
  userFullName: string,
  t: TFunction<"translation", undefined>,
) => {
  addCommentToItemInternal(item, userFullName, t("meeting_status.published"));
};

export const addDueDateChangeCommentToItemSimple = (
  item: MeetingItemRepresentation,
  userFullName: string,
  newDueDate: Date | undefined,
  t: TFunction<"translation", undefined>,
) => {
  const parsedDate = newDueDate ? new Date(newDueDate as any) : undefined;
  const newDateString =
    parsedDate && !isNaN(parsedDate.getTime())
      ? dateFormatter.format(parsedDate)
      : t("item.due_date.not_set");
  addCommentToItemInternal(
    item,
    userFullName,
    t("item.dueDate_changed") + ": " + newDateString,
  );
};

export const addRiskChangeCommentToItemSimple = (
  item: MeetingItemRepresentation,
  userFullName: string,
  newRisk: RiskIndicator,
  t: TFunction<"translation", undefined>,
) => {
  const newRiskString = t(`item_risk.${newRisk}`);
  addCommentToItemInternal(
    item,
    userFullName,
    t("item.risk_changed") + ": " + newRiskString,
  );
};

export const addContentChangeCommentToItemSimple = (
  item: MeetingItemRepresentation,
  userFullName: string,
  t: TFunction<"translation", undefined>,
) => {
  addCommentToItemInternal(item, userFullName, t("item.content_changed"));
};

export const addPrioritizationChangeCommentToItemSimple = (
  item: MeetingItemRepresentation,
  userFullName: string,
  newPrioritized: boolean,
  t: TFunction<"translation", undefined>,
) => {
  const newPrioritizedString = newPrioritized
    ? t("item_priority.high_priority")
    : t("item_priority.normal_priority");
  addCommentToItemInternal(
    item,
    userFullName,
    t("item.prioritization_changed") + ": " + newPrioritizedString,
  );
};

export const addActorChangeCommentToItemSimple = (
  item: MeetingItemRepresentation,
  userFullName: string,
  newActor: string,
  t: TFunction<"translation", undefined>,
) => {
  const newActorString = newActor || t("item.actor_not_set");
  addCommentToItemInternal(
    item,
    userFullName,
    t("item.actor_changed") + ": " + newActorString,
  );
};

const addCommentToItemInternal = (
  item: MeetingItemRepresentation | MeetingItem,
  userFullName: string,
  comment: string,
  separator?: string,
) => {
  if (!item.comments) {
    item.comments = "";
  }
  if (separator) {
    item.comments += separator + "\n";
  }
  item.comments += `[${userFullName} - ${dateTimeFormatter.format(new Date())}]: ${comment}`;
  if (separator) {
    item.comments += `\n` + separator;
  }
  item.comments += "\n";
};

export const ItemEditingPanelSimple: FC<ItemEditingPanelSimpleProps> = (
  props: ItemEditingPanelSimpleProps,
): ReactElement => {
  const { t } = useTranslation();
  const [content, setContent] = useState(props.meetingItem.content);
  const [category, setCategory] = useState(props.meetingItem.category);
  const [prioritized, setPrioritized] = useState(props.meetingItem.prioritized);
  const [status, setStatus] = useState(props.meetingItem.status);
  const [risk, setRisk] = useState(props.meetingItem.risk);
  const [actor, setActor] = useState(props.meetingItem.actor);
  const [dueDate, setDueDate] = useState(props.meetingItem.dueDate);
  const [comment, setComment] = useState("");
  const [showAttachments, setShowAttachments] = useState(false);
  const [attachmentsCount, setAttachmentsCount] = useState(props.meetingItem.attachments || 0);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [showPersonalNotesModal, setShowPersonalNotesModal] = useState(false);
  const [personalNotes, setPersonalNotes] = useState<PersonalNote[]>([]);
  const { instance } = useMsal();
  const appContext = useContext<AppContext>(Context);

  // Sync state when dialog opens or meetingItem changes
  useEffect(() => {
    if (props.open) {
      setContent(props.meetingItem.content);
      setCategory(props.meetingItem.category);
      setPrioritized(props.meetingItem.prioritized);
      setStatus(props.meetingItem.status);
      setRisk(props.meetingItem.risk);
      setActor(props.meetingItem.actor);
      setDueDate(props.meetingItem.dueDate);
      setComment("");
      setWarningDialogOpen(false);
    }
  }, [props.open, props.meetingItem]);

  // Memoize userFullName to avoid recalculation
  const userFullName = React.useMemo(
    () =>
      appContext.state.invoker.lastName && appContext.state.invoker.firstName
        ? `${appContext.state.invoker.firstName} ${appContext.state.invoker.lastName}`
        : instance.getActiveAccount()?.name,
    [
      appContext.state.invoker.firstName,
      appContext.state.invoker.lastName,
      instance,
    ],
  );

  // Memoize permission checks
  const isCreator = React.useMemo(
    () => props.meetingItem.creator === appContext.state.invoker.email,
    [props.meetingItem.creator, appContext.state.invoker.email],
  );

  const isActor = React.useMemo(
    () => props.meetingItem.actor === appContext.state.invoker.email,
    [props.meetingItem.actor, appContext.state.invoker.email],
  );

  const isResponsible = React.useMemo(
    () => isCreator || isActor,
    [isCreator, isActor],
  );
  const isCoachingTask = React.useMemo(
    () => Boolean(props.meetingItem.isCoachingTask),
    [props.meetingItem.isCoachingTask],
  );

  // Memoize edit permissions
  const editContent = React.useMemo(
    () => !props.readOnly && props.allowMetadataEditing && isResponsible,
    [props.readOnly, props.allowMetadataEditing, isResponsible],
  );

  const editCategory = React.useMemo(
    () =>
      !props.readOnly &&
      props.allowMetadataEditing &&
      isResponsible &&
      !isCoachingTask,
    [props.readOnly, props.allowMetadataEditing, isResponsible, isCoachingTask],
  );

  const editActor = React.useMemo(
    () => !props.readOnly && props.allowMetadataEditing && isCreator,
    [props.readOnly, props.allowMetadataEditing, isCreator],
  );

  const editDueDate = React.useMemo(
    () => !props.readOnly && (isCreator || (isActor && !prioritized)),
    [props.readOnly, isCreator, isActor, prioritized],
  );

  const editStatus = React.useMemo(
    () =>
      !props.readOnly && props.allowMetadataEditing && (isCreator || isActor),
    [props.readOnly, props.allowMetadataEditing, isCreator, isActor],
  );

  const hasChanges = React.useMemo(() => {
    return (
      props.meetingItem.content !== content ||
      props.meetingItem.category !== category ||
      props.meetingItem.dueDate !== dueDate ||
      props.meetingItem.status !== status ||
      props.meetingItem.prioritized !== prioritized ||
      props.meetingItem.actor !== actor ||
      props.meetingItem.risk !== risk
    );
  }, [
    content,
    category,
    dueDate,
    status,
    prioritized,
    actor,
    risk,
    props.meetingItem,
  ]);

  const handleWarningClose = React.useCallback(() => {
    if (hasChanges) {
      setWarningDialogOpen(true);
    } else {
      props.onClose();
    }
  }, [hasChanges, props.onClose]);

  const postComment = React.useCallback(() => {
    addCommentToItem(props.meetingItem, userFullName, comment, t);
    setComment("");
    props.onUpdate(props.meetingItem);
  }, [props.meetingItem, userFullName, comment, t, props.onUpdate]);

  const saveChanges = React.useCallback(async () => {
    // Delegate saving to parent to avoid duplicate PUTs
    const updatedRep: MeetingItemRepresentation = {
      ...props.meetingItem,
      content,
      prioritized,
      category,
      status,
      dueDate,
      actor,
      risk,
    } as MeetingItemRepresentation;
    if (comment.trim()) {
      addCommentToItem(updatedRep, userFullName, comment, t);
      setComment("");
    }
    try {
      await props.onUpdate(updatedRep);
      props.onClose();
    } catch (err) {
      // Error handling
    }
  }, [
    props.meetingItem,
    content,
    prioritized,
    category,
    status,
    dueDate,
    actor,
    risk,
    comment,
    userFullName,
    t,
    props.onUpdate,
    props.onClose,
  ]);

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="lg"
        onClose={handleWarningClose}
        PaperComponent={PaperComponent}
        aria-labelledby="customized-dialog-title"
        open={props.open}
        keepMounted={false} slotProps={{ paper: {
          style: {
            backgroundColor: "white",
          },
        } }}
      >
        <Box
          id="draggable-dialog-title"
          sx={{ display: "flex", alignItems: "center", cursor: "grab" }}
        >
          <DialogTitle
            sx={{ p: 2, fontWeight: 400 }}
            id="customized-dialog-title"
          >
            {t("item.editPanel")}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleWarningClose}
            sx={theme => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500]
            })}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: "1.2 1 0" }}>
              {/* item panel */}
              <Box>
                <Typography variant="button" sx={{
                  fontWeight: 600
                }}>
                  {t("item.details")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  columnGap: 2,
                  rowGap: 1,
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {/* creator---------------------------------------- */}
                <Typography
                  variant="button"
                  sx={theme => ({
                    color: theme.palette.secondary.main
                  })}
                >
                  {t("quicktask.creator")}:
                </Typography>
                <Typography variant="body2">
                  {props.meetingItem.creator || t("item.notAvailable")}
                </Typography>
                <Divider sx={{ gridColumn: "1 / -1", width: "100%", my: 1 }} />

                {/* category--------------------------------------- */}
                <Typography
                  variant="button"
                  sx={theme => ({
                    color: theme.palette.secondary.main
                  })}
                >
                  {t("item.category")}:
                </Typography>
                {editCategory ? (
                  <TextField
                    className="lighter-textfield"
                    id="category"
                    value={category || ""}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setCategory(event.target.value)
                    }
                  />
                ) : (
                  <Typography variant="body2">
                    {category || t("item.notAvailable")}
                  </Typography>
                )}

                {/* what------------------------------------------ */}
                <Typography
                  variant="button"
                  sx={theme => ({
                    color: theme.palette.secondary.main
                  })}
                >
                  {t("item.content")}:
                </Typography>
                {editContent ? (
                  <TextField
                    className="lighter-textfield"
                    sx={{
                      flexGrow: 1,
                    }}
                    id="itemDescription"
                    fullWidth={true}
                    autoFocus={true}
                    required={true}
                    multiline
                    rows={3}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                  />
                ) : (
                  <Typography variant="body2">{content}</Typography>
                )}

                {/* actor------------------------------------------ */}
                <Typography
                  variant="button"
                  sx={theme => ({
                    color: theme.palette.secondary.main
                  })}
                >
                  {t("item.actor")}:
                </Typography>
                {props.meetingItem.actor ? (
                  editActor ? (
                    <FreeSingleSelect
                      onValueSelected={(value) => {
                        setActor(value);
                      }}
                      options={[
                        ...(appContext.state.selectedList?.participants || []),
                        ...(appContext.state.invoker?.invitedUserEmails || []),
                      ]}
                      value={actor}
                      disabled={!props.meetingItem.task}
                      sx={{ width: 300 }}
                    />
                  ) : (
                    <Typography variant="body2">
                      {props.meetingItem.actor}
                    </Typography>
                  )
                ) : (
                  <Typography variant="body2">
                    {t("item.notAvailable")}
                  </Typography>
                )}

                {/* duedate--------------------------- */}
                <Typography
                  variant="button"
                  sx={theme => ({
                    color: theme.palette.secondary.main
                  })}
                >
                  {t("item.dueDate")}:
                </Typography>

                {props.meetingItem.dueDate ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Tooltip
                        title={
                          !editDueDate && prioritized
                            ? t("item.tooltipfixedByCreator")
                            : ""
                        }
                        placement="top"
                      >
                        <Box>
                          <DatePicker
                            className="lighter-textfield"
                            disabled={!editDueDate}
                            slotProps={{
                              textField: {
                                size: "small",
                                error: !editDueDate && prioritized,
                              },
                            }}
                            sx={{ minWidth: 200, maxWidth: 200 }}
                            value={dayjs(dueDate)}
                            onChange={(newDate) => setDueDate(newDate.toDate())}
                          />
                        </Box>
                      </Tooltip>
                      {/* show Priority Switch for Creator only and when due date is Fixed by Creator show warning for Actor */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 200,
                        }}
                      >
                        {!editDueDate && prioritized && isActor ? (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.75rem",
                              fontStyle: "italic",
                            }}
                          >
                            {t("item.fixedByCreator")}
                          </Typography>
                        ) : (
                          isCreator && (
                            <>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.8rem" }}
                              >
                                {t("item.fixedDueDate")}
                              </Typography>
                              <Tooltip
                                title={t("item.fixedDueDateInfo")}
                                placement="top"
                              >
                                <Switch
                                  size="medium"
                                  checked={prioritized}
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                  ) => setPrioritized(event.target.checked)}
                                  color="primary"
                                  disabled={!editDueDate}
                                />
                              </Tooltip>
                            </>
                          )
                        )}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2">
                    {t("item.notAvailable")}
                  </Typography>
                )}

                {/* status---------------------------- */}
                <Typography
                  variant="button"
                  sx={theme => ({
                    color: theme.palette.secondary.main
                  })}
                >
                  {t("item.status")}:
                </Typography>
                {props.meetingItem.task ? (
                  <Box sx={{ width: "150px" }}>
                    <ItemStatus
                      status={status}
                      lastModifiedBy={props.meetingItem.actor}
                      onChange={
                        editStatus
                          ? (newStatus) => setStatus(newStatus)
                          : undefined
                      }
                      readOnly={!editStatus}
                    />
                  </Box>
                ) : (
                  <Box>{t("item.notAvailable")}</Box>
                )}

                {/* Risk---------------------------- */}
                {props.meetingItem.task && (
                  <Typography
                    variant="button"
                    sx={theme => ({
                      color: theme.palette.secondary.main
                    })}
                  >
                    {t("item.riskScore")}:
                  </Typography>
                )}
                {props.meetingItem.task && (
                  <Box sx={{ fontSize: "0.875rem" }}>
                    <FormControl sx={{ width: 300 }}>
                      <Select
                        className="lighter-textfield"
                        disabled={
                          props.readOnly ||
                          !props.allowMetadataEditing ||
                          (!isCreator && !isActor)
                        }
                        value={risk}
                        onChange={(event: { target: { value: string } }) => {
                          {
                            setRisk(
                              enumFromStringValue(
                                RiskIndicator,
                                event.target.value,
                              ),
                            );
                          }
                        }}
                      >
                        <MenuItem value={RiskIndicator.NoRisk}>
                          {t(`item_risk.${RiskIndicator.NoRisk}`)}
                        </MenuItem>
                        <MenuItem value={RiskIndicator.OnTrack}>
                          {t(`item_risk.${RiskIndicator.OnTrack}`)}
                        </MenuItem>
                        <MenuItem value={RiskIndicator.AtRisk}>
                          {t(`item_risk.${RiskIndicator.AtRisk}`)}
                        </MenuItem>
                        <MenuItem value={RiskIndicator.Roadblock}>
                          {t(`item_risk.${RiskIndicator.Roadblock}`)}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ flex: "1 1 0" }}>
              <Box>
                <Typography variant="button" sx={{
                  fontWeight: 600
                }}>
                  {t("item.comments")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Box>

              <TextField
                sx={theme => ({
                  "& .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: theme.palette.background.paper,
                  },

                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: theme.palette.primary.main,
                  }
                })}
                variant="outlined"
                id="itemComments"
                // label={t('item.comments').toUpperCase()}
                multiline
                fullWidth={true}
                disabled={true}
                rows={11}
                value={props.meetingItem.comments}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: 2,
                  gap: 1,
                }}
              >
                <TextField
                  sx={{
                    flexGrow: 1,
                  }}
                  id="addNewItemComment"
                  label={t("item.addComment")}
                  multiline
                  fullWidth={true}
                  rows={2}
                  value={comment}
                  disabled={props.readOnly}
                  onChange={(e) => setComment(e.target.value)}
                />
                <IconButton
                  aria-label="post"
                  onClick={postComment}
                  disabled={props.readOnly || !comment.trim()}
                  sx={theme => ({
                    color: theme.palette.primary.main
                  })}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <Divider sx={{ mb: 1 }} />
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<LockOutlinedIcon />}
            onClick={() => setShowPersonalNotesModal(true)}
          >
            {t("meetingNotes.personalNotes.sectionTitle")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AttachFileIcon />}
            onClick={() => setShowAttachments(true)}
          >
            {t("dashboard.itemAttachments_label", { count: attachmentsCount })}
          </Button>
          <Button
            variant="contained"
            autoFocus
            disabled={props.readOnly || !content.trim()}
            onClick={saveChanges}
          >
            {t("item.saveChanges")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Attachments Modal */}
      <AttachmentsModal
        open={showAttachments}
        allowModifications={props.allowAttachmentsEditing}
        height="70vh"
        uploadPrefix={
          props.meetingItem.meetingId
            ? `items/meetings/${props.meetingItem.meetingId}/items/${props.meetingItem.id}/`
            : props.meetingItem.sessionId
              ? `sessions/${props.meetingItem.sessionId}/tasks/${props.meetingItem.id}/`
              : `items/quicktasks/${props.meetingItem.id}/`
        }
        meetingId={props.meetingItem.meetingId}
        itemId={props.meetingItem.id}
        sessionId={props.meetingItem.sessionId}
        taskId={props.meetingItem.id}
        onClose={() => setShowAttachments(false)}
        onAttachmentsCountChanged={(
          count: number,
          meetingId?: string,
          itemId?: string,
          sessionId?: string,
          taskId?: string,
        ) => {
          props.meetingItem.attachments = count;
          setAttachmentsCount(count);
          props.onAttachmentsCountChanged?.(
            count,
            meetingId,
            itemId,
            sessionId,
            taskId,
          );
        }}
      />
      {/* Personal Notes Modal */}
      <PersonalNotesModal
        open={showPersonalNotesModal}
        onClose={() => setShowPersonalNotesModal(false)}
        targetType={
          props.meetingItem.meetingId
            ? PersonalNoteTargetType.MeetingItem
            : props.meetingItem.isCoachingTask
              ? PersonalNoteTargetType.CoachingTask
              : PersonalNoteTargetType.QuickTask
        }
        targetId={props.meetingItem.id || null}
        targetItem={props.meetingItem}
        onNotesChange={(notes) => setPersonalNotes(notes)}
      />
      {/* Unsaved Changes Warning Dialog */}
      <ConfirmationPanel
        action="warning"
        showModal={warningDialogOpen}
        hideModal={() => setWarningDialogOpen(false)}
        confirmModal={() => {
          setWarningDialogOpen(false);
          props.onClose();
        }}
      />
    </>
  );
};
